import React, { useEffect, useMemo, useState, useRef, useContext } from 'react';
import Counter from './Counter';
import './Notifications.css';
import { FaArrowLeft, FaTimes, FaCheck } from 'react-icons/fa';
import { FiSettings, FiSearch, FiDollarSign, FiRefreshCw } from 'react-icons/fi';
import { IoWarningOutline, IoInformationCircleOutline } from 'react-icons/io5';
import { TbTrendingDown } from 'react-icons/tb';
import notificationsApi from '../services/notificationsApi';
import UnitMappingService from '../services/unitMappingService';
import { useNotificationCount } from '../services/useNotificationCount';
import http from '../services/httpClient';
import AuthContext from '../contexts/AuthContext';
import { StandardSearch, StandardScrollStyles, highlightMatch, performSmartSearch, standardTextSizes } from '../styles/standardStyles';
import { useTranslate } from '../translations/useTranslate';
import { translateNotificationText, translateNotificationType } from '../utils/notificationTranslator';

const Notifications = ({ goTo }) => {
  const { user } = useContext(AuthContext);
  const { t, language } = useTranslate(); // Add language to track current language
  
  // Resolve identity/tenant from auth context (fallback to deprecated methods)
  const businessId = useMemo(() => user?.businessId || user?.business_id, [user]);
  const userId = useMemo(() => user?.user_id || user?.id || 1, [user]);

  // Helper function to properly handle image URLs (GCS, local, relative)
  const getProxiedImageUrl = (imageUrl) => {
    if (!imageUrl) {
      console.warn('⚠️ getProxiedImageUrl called with null/undefined imageUrl');
      return null;
    }
    
    // Check if it's a Google Cloud Storage URL
    const isGcsUrl = imageUrl.includes('storage.googleapis.com') || imageUrl.includes('.storage.googleapis.com');
    
    if (isGcsUrl) {
      return imageUrl;
    }
    
    // For absolute URLs (http:// or https://), use directly
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // For relative URLs starting with /uploads, backend should have converted these to GCS
    // If we still get relative paths, log a warning and try to construct GCS URL
    if (imageUrl.startsWith('/uploads')) {
      const gcsBaseUrl = 'https://storage.googleapis.com/menuimages001';
      const gcsUrl = `${gcsBaseUrl}${imageUrl}`;
      console.warn('⚠️ Received relative path, converting to GCS:', imageUrl, '→', gcsUrl);
      return gcsUrl;
    }
    
    return imageUrl;
  };

  // State
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const [detailsOpenId, setDetailsOpenId] = useState(null);
  const [detailsData, setDetailsData] = useState({ status: 'idle', kind: null, rows: [], extra: {} });
  
  // Search handlers
  const clearSearch = () => {
    setSearchQuery('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };
  
  // Use notification count hook
  const { markAsRead, markAllAsRead: updateGlobalCount, fetchUnreadCount } = useNotificationCount();

  const loadNotifications = async () => {
    try {
      setLoading(true);
      
  // Do not auto-create or sync other notifications here to avoid noise.
      
      const res = await notificationsApi.getNotifications(businessId, { userId, status: 'all', limit: 100 });
      if (res && res.success) {
        const list = (res.data || []).map((n) => ({
          id: n.notification_id,
          type: n.type || 'info',
          title: n.title,
          description: n.description,
          isRead: !!n.is_read,
          created_at: n.created_at,
          // Keep only explicit action text from backend; no default 'View'
          actionText: n.action_text || null,
          related_url: n.related_url,
          image_url: n.image_url || null,
        }));
        setNotifications(list);
      } else {
        console.warn('No notifications or failed response:', res);
        setNotifications([]);
      }
    } catch (e) {
      console.error('Failed to load notifications', e);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Just load notifications on mount
    loadNotifications();
    
    // Listen for refresh events from other components
    const handleRefresh = () => {
      loadNotifications();
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('refreshNotifications', handleRefresh);
      return () => window.removeEventListener('refreshNotifications', handleRefresh);
    }
  }, []);

  // Remove auto-polling (removed the 15-second interval)

  const filtered = useMemo(() => {
    return searchQuery.trim() 
      ? performSmartSearch(notifications, searchQuery, ['title', 'description'])
      : notifications;
  }, [notifications, searchQuery]);

  const markOneRead = async (id) => {
    try {
      await notificationsApi.markRead(userId, [id]);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
      markAsRead(id); // Update global count
    } catch (e) {
      console.error('Failed to mark read', e);
    }
  };

  const removeNotification = async (id) => {
    try {
      await notificationsApi.dismissNotifications([id]);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      fetchUnreadCount(); // Refresh global count
    } catch (e) {
      console.error('Failed to dismiss notification', e);
    }
  };

  const markAllAsRead = async () => {
    // Optimistic update: immediately clear all from UI
    const ids = notifications.map((n) => n.id);
    setNotifications([]);
    updateGlobalCount(); // Update global count to 0
    try {
      await notificationsApi.markAllRead(userId, businessId);
      if (ids.length > 0) {
        await notificationsApi.dismissNotifications(ids);
      }
    } catch (e) {
      console.error('Failed to mark all as read', e);
      // On error, reload so the UI is back in sync
      loadNotifications();
    }
  };


  const dismissAll = async () => {
    try {
      const ids = filtered.map((n) => n.id);
      if (ids.length === 0) return;
      await notificationsApi.dismissNotifications(ids);
      setNotifications((prev) => prev.filter((n) => !ids.includes(n.id)));
    } catch (e) {
      console.error('Failed to dismiss all', e);
    }
  };
  
  const createTestNotification = async () => {
    try {
      const response = await http.post('/notifications/test', { 
        businessId, 
        userId, 
        message: `Test notification created at ${new Date().toLocaleTimeString()}` 
      });
      if (response.success) {
        // Refresh notifications to show the new one
        loadNotifications();
      }
    } catch (e) {
      console.error('Failed to create test notification', e);
    }
  };

  // Map notification title -> details fetcher and label
  const resolveDetailsKind = (title = '') => {
    const t = title.toLowerCase();
    if (t.startsWith('recipe-stock discrepancy') || t.includes('recipe-stock discrepancies')) return 'stock';
    if (t.startsWith('recipe updated') || t.includes('recent recipe changes')) return 'recent';
    if (t.startsWith('zero-cost recipe') || t.includes('zero-cost recipes')) return 'zero';
    if (t.startsWith('unmapped ingredient') || t.includes('unmapped ingredient units')) return 'unmapped';
    if (t.startsWith('incomplete recipe') || t.includes('incomplete recipes')) return 'incomplete';
    return null;
  };

  const parseDishFromTitle = (title = '') => {
    const m = title.split(':');
    if (m.length >= 2) return m.slice(1).join(':').trim();
    return null;
  };

  const loadDetails = async (notif) => {
    const kind = resolveDetailsKind(notif.title);
    if (!kind) return;
    setDetailsOpenId(notif.id);
    setDetailsData({ status: 'loading', kind, rows: [], extra: {} });
    try {
      let res;
      switch (kind) {
        case 'stock':
          res = await notificationsApi.getStockDiscrepancyDetails(businessId, 1, 200);
          break;
        case 'recent':
          res = await notificationsApi.getRecentRecipeChangeDetails(businessId, 7, 200);
          break;
        case 'zero':
          res = await notificationsApi.getZeroCostRecipeDetails(businessId, 200);
          break;
        case 'unmapped':
          res = await notificationsApi.getUnmappedIngredientDetails(businessId, 200);
          break;
        case 'incomplete':
          res = await notificationsApi.getIncompleteRecipeDetails(businessId, 200);
          break;
        default:
          res = null;
      }
      if (res && res.success) {
        let rows = res.data || [];
        const dish = parseDishFromTitle(notif.title);
        if (dish) {
          // Try to narrow to that dish when payload contains dish fields
          rows = rows.filter((r) =>
            (r.dish_name && String(r.dish_name).toLowerCase() === dish.toLowerCase())
          );
        }
        setDetailsData({ status: 'ready', kind, rows, extra: { count: res.count, servings: res.servings } });
      } else {
        setDetailsData({ status: 'error', kind, rows: [], extra: { message: 'No details' } });
      }
    } catch (e) {
      console.error('Failed to load details', e);
      setDetailsData({ status: 'error', kind, rows: [], extra: { message: e.message } });
    }
  };

  const renderDetails = () => {
    if (!detailsOpenId) return null;
    const { status, kind, rows, extra } = detailsData;
    return (
      <div className="notification-details-panel">
        {status === 'loading' && <div style={{ padding: '8px 12px' }}>{t('notifications.loadingDetails')}</div>}
        {status === 'error' && <div style={{ padding: '8px 12px', color: '#b00' }}>{t('notifications.failedToLoadDetails')} {extra?.message}</div>}
        {status === 'ready' && (
          <div style={{ padding: '8px 12px' }}>
            {kind === 'stock' && (
              <ul className="details-list">
                {rows.map((r) => (
                  <li key={`d-${r.dish_id}`}> 
                    <b>{r.dish_name}</b> — {t('notifications.shortOn')} {r.shortage_count} {t('notifications.ingredients')}
                    {Array.isArray(r.shortages) && r.shortages.length > 0 && (
                      <ul className="sub-list">
                        {r.shortages.slice(0, 5).map((s, idx) => (
                          <li key={`i-${r.dish_id}-${idx}`}>{s.ingredient_name}: {t('notifications.need')} {s.required_qty} {s.unit_label}, {t('notifications.have')} {s.current_stock} {s.unit_label}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {kind === 'recent' && (
              <ul className="details-list">
                {rows.map((r) => (
                  <li key={`rc-${r.menu_item_id}`}>
                    <b>{r.dish_name}</b> — {t('notifications.updated')} {new Date(r.updated_at).toLocaleDateString()}
                    {Array.isArray(r.changed_ingredients) && r.changed_ingredients.length > 0 && (
                      <ul className="sub-list">
                        {r.changed_ingredients.slice(0, 6).map((ci, idx) => (
                          <li key={`rc-${r.menu_item_id}-${idx}`}>
                            {ci.ingredient_name}: {ci.change_type}{ci.quantity ? ` → ${ci.quantity} ${ci.unit_label || ''}` : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {kind === 'zero' && (
              <ul className="details-list">
                {rows.map((r) => (
                  <li key={`zc-${r.menu_item_id}`}><b>{r.dish_name}</b> — estimated cost: {r.estimated_cost ?? '0'}</li>
                ))}
              </ul>
            )}
            {kind === 'unmapped' && (
              <ul className="details-list">
                {rows.map((r, idx) => (
                  <li key={`um-${r.dish_id}-${r.item_id}-${idx}`}>
                    <b>{r.ingredient_name}</b> in <i>{r.dish_name}</i>: recipe unit {r.recipe_unit_symbol || r.recipe_unit_id} → inventory {r.standard_unit_symbol || r.standard_unit_id}
                  </li>
                ))}
              </ul>
            )}
            {kind === 'incomplete' && (
              <ul className="details-list">
                {rows.map((r) => (
                  <li key={`ic-${r.menu_item_id}`}>
                    <b>{r.dish_name}</b> — {r.no_recipe ? 'no recipe' : `${r.ingredient_count} ingredients`} {r.invalid_count > 0 ? `(invalid: ${r.invalid_count})` : ''}
                  </li>
                ))}
              </ul>
            )}
            <div style={{ fontSize: 12, color: '#777', marginTop: 8 }}>
              Items: {(
                <Counter
                  value={Number(rows.length || 0)}
                  places={(() => { const n = Number(rows.length || 0); if (n >= 100) return [100,10,1]; if (n>=10) return [10,1]; return [1]; })()}
                  fontSize={10}
                  padding={0}
                  gap={0}
                  horizontalPadding={0}
                  borderRadius={0}
                  textColor={'currentColor'}
                  gradientHeight={0}
                  containerStyle={{ display: 'inline-block', verticalAlign: 'baseline' }}
                  counterStyle={{ lineHeight: 1 }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const getNotificationIcon = (type) => {
    const t = (type || 'info').toLowerCase();
    switch (t) {
      case 'critical':
        return <IoWarningOutline />;
      case 'warning':
        return <TbTrendingDown />;
      case 'info':
      case 'success':
      default:
        return <IoInformationCircleOutline />;
    }
  };

  const getNotificationTag = (notification) => {
    const notifType = (notification.type || 'info').toLowerCase();
    // Check if it's a unit mapping notification by title/description
    const isUnitMapping = notification.title && (
      notification.title.includes('Unit Mapping') ||
      notification.title.includes('Kitchen Units') ||
      notification.title.includes('Supplier Conversions') ||
      notification.title.includes('Unit Conversion') ||
      notification.title.includes('Unmapped Unit')
    );

    if (isUnitMapping) {
      if (notifType === 'success') return 'Unit Setup';
      if (notifType === 'warning') return 'Unit Config';
      return 'Unit Mapping';
    }

    // Default behavior for other notifications
    switch (notifType) {
      case 'success':
        return 'Done';
      case 'warning':
        return 'Low Stock';
      case 'critical':
        return 'Critical';
      default:
        return 'Info';
    }
  };

  const stripMessageIcons = (text = '') => {
    return String(text)
      .replace(/[\uFE0E\uFE0F]/g, '')
      .replace(/[\u200D\u20E3]/g, '')
      .replace(/\p{Extended_Pictographic}|\p{Emoji_Presentation}/gu, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  };

  const handleNotificationClick = (notification) => {
    // Only redirect; do NOT mark as read here. Use the ✓ button to mark read.
    if (notification.related_url && goTo) {
      const url = notification.related_url;
      
      // Map backend URLs to frontend screen names based on App.js routes
      if (url.includes('/settings')) {
        goTo('settings');
      } else if (url.includes('/map2')) {
        goTo('map2');
      } else if (url.includes('/map')) {
        goTo('map');
      } else if (url.includes('/overview')) {
        goTo('overview');
      } else if (url.includes('/abc')) {
        // ABC module removed; redirect to modern XYZ implementation
        goTo('xyz');
      } else if (url.includes('/vendors')) {
        goTo('vendors');
      } else if (url.includes('/todays-sales-report')) {
        goTo('todays-sales-report');
      } else if (url.includes('/wastage-reports')) {
        // No direct wastage-reports screen in App.js, navigate to reports
        goTo('reports');
      } else if (url.includes('/reports/daily')) {
        goTo('reports');
      } else if (url.includes('/stock-in')) {
        goTo('stock-in');
      } else if (url.includes('/dashboard')) {
        goTo('dashboard');
      } else if (url.includes('/inventory')) {
        const focusMatch = url.match(/focus=([^&]+)/);
        const focusItem = focusMatch ? focusMatch[1] : null;
        goTo('overview', { focus: focusItem }); // Use 'overview' as inventory screen
      } else {
        // Try to extract the route and use it directly
        const routeMatch = url.match(/^\/([^?]+)/);
        if (routeMatch) {
          const route = routeMatch[1];
          // Check if this route exists in App.js screens
          const validRoutes = ['dashboard', 'overview', 'map', 'map1', 'map2', 'map3', 'itemmap', 
                              'stock-in', 'stock-out', 'usage', 'todays-sales-report', 'ocr', 'vendors', 
                              'minimal-stock', 'create-reorder', 'settings', 'reports', 'sales-report'];
          if (validRoutes.includes(route)) {
            goTo(route);
          } else {
            console.warn('Unknown route:', route, 'defaulting to dashboard');
            goTo('dashboard');
          }
        }
      }
    }
  };

  // Helper: treat vendor credit notifications as info-styled
  const isVendorCredit = (n) => {
    const title = (n?.title || '').toLowerCase();
    const desc = (n?.description || '').toLowerCase();
    return title.startsWith('new vendor credit added') || desc.includes('vendor credit') || desc.includes('credit of');
  };

  return (
    <div>
      <StandardScrollStyles />
      {/* Main Notifications Page */}
      <div className="notifications-page-container" style={{ maxWidth: '600px', margin: '0 auto' }}>
        <header className="notifications-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
            <FaArrowLeft 
              className="header-icon" 
              onClick={() => goTo && goTo('dashboard')} 
              style={{ cursor: 'pointer' }}
              title={t('notifications.backToDashboard')}
            />
            <h1 style={{ color: '#000000' }}>{t('notifications.title')}</h1>
          </div>
          {filtered.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginRight: '3px' }}>
              <FaCheck 
                className="header-icon" 
                onClick={markAllAsRead}
                style={{ cursor: 'pointer' }}
                title={t('notifications.markAllAsRead')}
              />
            </div>
          )}
        </header>

        <section className="search-filter-bar">
          <StandardSearch
            value={searchQuery}
            onChange={setSearchQuery}
            onClear={clearSearch}
            placeholder={t('notifications.searchPlaceholder')}
            inputRef={searchInputRef}
          />
        </section>

        <main className="notifications-list">
          {loading && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              padding: '40px 20px',
              gap: '16px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{
                color: '#666',
                fontSize: '16px',
                textAlign: 'center',
                margin: 0
              }}>{t('notifications.loading')}</p>
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '60vh',
              fontSize: '16px',
              color: '#888'
            }}>
              {t('notifications.noNotifications')}
            </div>
          )}

          {!loading &&
            filtered.map((notification) => {
              const effectiveType = (isVendorCredit(notification) ? 'info' : (notification.type || 'info')).toLowerCase();
              return (
              <div
                key={notification.id}
                className={`notification-item-card ${effectiveType} ${notification.isRead ? 'is-read' : ''}`}
                onClick={() => handleNotificationClick(notification)}
                style={{ cursor: notification.isRead ? 'default' : 'pointer' }}
              >
                <div className={`notification-icon-wrapper ${effectiveType}`}>
                  {getNotificationIcon(effectiveType)}
                </div>
                <div className="notification-content">
                  <p className="notification-title" style={{...standardTextSizes.base,fontSize: '16px', fontWeight: '600', color: '#000000'}}>
                    {highlightMatch(stripMessageIcons(translateNotificationText(notification.title, t, language)), searchQuery)}
                  </p>
                  <p className="notification-description" style={{ ...standardTextSizes.sm, marginBottom: '8px',fontSize: '14px', color: '#374151',fontWeight: '200' }}>
                    {highlightMatch(stripMessageIcons(translateNotificationText(notification.description, t, language)), searchQuery)}
                  </p>
                  {notification.image_url && (
                    <div style={{ 
                      marginTop: '8px', 
                      marginBottom: '8px',
                      width: '100%',
                      minHeight: '100px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <img 
                        src={getProxiedImageUrl(notification.image_url)} 
                        alt="Evidence photo"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          width: 'auto',
                          height: 'auto',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          objectFit: 'contain',
                          cursor: 'pointer',
                          display: 'block',
                          visibility: 'visible',
                          opacity: 1
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = getProxiedImageUrl(notification.image_url);
                          if (url) window.open(url, '_blank');
                        }}
                        onLoad={(e) => {
                          const img = e.target;
                          // Flash border to visually confirm image is there
                          img.style.border = '3px solid #10b981';
                          setTimeout(() => { img.style.border = '1px solid #e5e7eb'; }, 1000);
                        }}
                        onError={(e) => {
                          console.error('❌ Failed to load notification image');
                          console.error('   Original URL:', notification.image_url);
                          console.error('   Attempted URL:', e.target.src);
                          console.error('   Notification ID:', notification.id);
                          console.error('   Issue: Image may be on Google Cloud Storage but not accessible, or the file was deleted');
                          // Hide the broken image
                          e.target.style.display = 'none';
                          // Show a helpful error message
                          const parent = e.target.parentElement;
                          if (parent && !parent.querySelector('.image-error-msg')) {
                            const errorMsg = document.createElement('div');
                            errorMsg.className = 'image-error-msg';
                            errorMsg.style.cssText = 'padding: 12px; font-size: 13px; text-align: center; margin-top: 8px;';
                            const isGcsUrl = notification.image_url?.includes('storage.googleapis.com');
                            errorMsg.innerHTML = `
                              <div style="font-weight: 600; margin-bottom: 4px; text-align: left;">Image Not Available</div>
                            `;
                            parent.appendChild(errorMsg);
                          }
                        }}
                      />
                    </div>
                  )}
                  <div className="notification-meta" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span
                      className={`notification-tag tag-${effectiveType}`}
                    >
                      {translateNotificationType(getNotificationTag(notification), t)}
                    </span>
                    <span style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap', paddingRight: '8px' }}>
                      {(() => {
                        const date = new Date(notification.created_at);
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        return `${day}/${month}/${year}`;
                      })()}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-end' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeNotification(notification.id);
                    }}
                    className="notification-dismiss-btn"
                    title={t('notifications.dismiss')}>
                    <FaTimes />
                  </button>
                </div>
              </div>
            );})}
        </main>

        <footer className="notifications-footer">
        </footer>
      </div>
    </div>
  );
};

export default Notifications;
